import { useEffect, useMemo, useState } from 'react';
import type { ModuleParameter } from '../types';
import { useLookup, type LookupItem, type LookupKind } from '../lookups';
import { DbLookupEditor } from './DbLookupEditor';
import { DbLookupScheduleEditor } from './DbLookupScheduleEditor';
import { VariableListEditor } from './VariableListEditor';
import { SlotMappingEditor } from './SlotMappingEditor';
import { PromptTextEditor } from './PromptTextEditor';
import { VariableCreateModal } from './VariableCreateModal';

// Sentinel select value for the "+ New variable…" option in variable
// pickers. Picked to never collide with a real variable id (which are
// numeric, including negative ids for system variables).
const NEW_VARIABLE_OPTION = '__fb_new_variable__';

// Kind switching — maps the parameter `TypeId` to the default `kind` (the
// value stored in NodeParameter.valueType in the DB) and the alternate kinds
// the user may switch to. This mirrors the per-editor `setAsXxx` methods in
// the legacy CustomerApplicationFlowNodeDetailsViewcontroller.js.

export type ParamKind =
  | 'string'
  | 'int'
  | 'destination'
  | 'variable'
  | 'voicefile'
  | 'huntgroup'
  | 'agent'
  | 'weekplan'
  | 'calendar'
  | 'flow'
  | 'application'
  | 'bit'
  | 'dtmf'
  | 'date'
  | 'time'
  | 'hidden'
  | 'divider'
  | 'simpledb'
  | 'simpledbschedule'
  | 'variablelist'
  | 'kvp'
  // Voicebot-era parameter kinds (resolved by ModuleParameterType.Description
  // rather than a hardcoded ModuleParameterTypeId — the spec explicitly
  // says new typeIds are assigned at migration time and must not be
  // hardcoded in the SPA).
  | 'recognizer'
  | 'language'
  | 'voicepreset'
  | 'slotmapping'
  // Spoken text (Mod_VB_Say.Text, Mod_VB_Ask.PromptText/ProcessingText):
  // multiline editor with ${VARIABLE} placeholders. Stored as valueType
  // 'string' so the Lua side keeps reading it as plain text.
  | 'prompttext';

interface KindOption {
  kind: ParamKind;
  label: string;
}

// Description-based fallback for parameter kinds. New ModuleParameterType
// rows (Recognizer, Language, VoicePreset, ...) get their ids assigned at
// migration time, so we resolve them by `ModuleParameterType.Description`
// (== the catalog's `typeName` per parameter) instead of hardcoding the
// integer id. Returns `null` when the description isn't recognised so the
// caller can fall through to its existing TypeId-based switch.
function kindForTypeName(typeName: string | null | undefined): ParamKind | null {
  switch ((typeName ?? '').trim().toLowerCase()) {
    case 'recognizer':
      return 'recognizer';
    case 'language':
      return 'language';
    case 'voicepreset':
    case 'voice preset':
      return 'voicepreset';
    case 'slotmapping':
      return 'slotmapping';
    case 'prompttext':
    case 'prompt text':
      return 'prompttext';
    default:
      return null;
  }
}

// What the editor allows the user to pick. Most non-trivial primitive types
// can be promoted to a Variable reference.
function kindsForType(typeId: number, typeName?: string | null): KindOption[] {
  // New description-resolved types first; if recognised we return early
  // so the legacy TypeId switch below stays untouched.
  const byName = kindForTypeName(typeName);
  if (byName === 'recognizer') {
    return [
      { kind: 'recognizer', label: 'Recognizer' },
      { kind: 'variable', label: 'Variable' },
    ];
  }
  if (byName === 'language') {
    return [
      { kind: 'language', label: 'Language' },
      { kind: 'variable', label: 'Variable' },
    ];
  }
  if (byName === 'voicepreset') {
    return [
      { kind: 'voicepreset', label: 'Voice preset' },
      { kind: 'variable', label: 'Variable' },
    ];
  }
  if (byName === 'slotmapping') {
    // Not promotable to a plain variable — it's a per-slot mapping editor.
    return [{ kind: 'slotmapping', label: 'Slot mapping' }];
  }
  if (byName === 'prompttext') {
    return PROMPT_TEXT_KINDS;
  }
  switch (typeId) {
    case 1:
      return [
        { kind: 'string', label: 'String' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 2:
      return [
        { kind: 'int', label: 'Int' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 3:
      return [
        { kind: 'huntgroup', label: 'Huntgroup' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 4:
      return [
        { kind: 'destination', label: 'Destination' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 9:
      return [
        { kind: 'voicefile', label: 'Voice' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 10:
      return [
        { kind: 'weekplan', label: 'Weekplan' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 11:
      return [
        { kind: 'agent', label: 'Agent' },
        { kind: 'variable', label: 'Variable' },
      ];
    case 12:
      return [
        { kind: 'calendar', label: 'Calendar' },
        { kind: 'variable', label: 'Variable' },
      ];
    default:
      return [];
  }
}

const PROMPT_TEXT_KINDS: KindOption[] = [
  { kind: 'prompttext', label: 'Text' },
  { kind: 'variable', label: 'Variable' },
];

/** NodeParameter.valueType to persist for an editor kind. The prompt editor
 *  is a richer UI over a plain string, so it stores as 'string'. */
function storedKindFor(kind: ParamKind): ParamKind {
  return kind === 'prompttext' ? 'string' : kind;
}

export function defaultKindFor(typeId: number, typeName: string | null | undefined): ParamKind {
  // Resolve Recognizer / Language / VoicePreset by description first so
  // the migration is free to pick any ModuleParameterTypeId for them.
  const byName = kindForTypeName(typeName);
  if (byName) return byName;
  switch (typeId) {
    case 1: return 'string';
    case 2: return 'int';
    case 3: return 'huntgroup';
    case 4: return 'destination';
    case 5: return 'variable';
    case 6: return 'flow';
    case 7: return 'application';
    case 8: return 'bit';
    case 9: return 'voicefile';
    case 10: return 'weekplan';
    case 11: return 'agent';
    case 12: return 'calendar';
    case 13: return 'simpledb';
    case 14: return 'divider';
    case 15: return 'hidden';
    case 16: return 'dtmf';
    case 17: return 'variablelist';
    case 18: return 'kvp';
    case 19: return 'date';
    case 20: return 'time';
    case 21: return 'simpledbschedule';
    // XML-RPC Mappings (TypeId 22) stores `{id_1, id_2, ...}` exactly like
    // Save Variables (TypeId 17), so it shares the variablelist editor.
    case 22: return 'variablelist';
    default:
      return ((typeName || 'string').toLowerCase()) as ParamKind;
  }
}

const DTMF_OPTIONS: Array<{ id: string; name: string }> = [
  { id: '', name: 'Select' },
  { id: 'none', name: 'None' },
  { id: '0', name: '0' },
  { id: '1', name: '1' },
  { id: '2', name: '2' },
  { id: '3', name: '3' },
  { id: '4', name: '4' },
  { id: '5', name: '5' },
  { id: '6', name: '6' },
  { id: '7', name: '7' },
  { id: '8', name: '8' },
  { id: '9', name: '9' },
  { id: '*', name: '*' },
  { id: '#', name: '#' },
  { id: 'any', name: 'Any' },
];

/**
 * Sibling parameters on the same node, keyed by typeName (the lowercase
 * ModuleParameterType.Description). Used by editors that need to know
 * what the user picked for *another* field — e.g. the Voice preset
 * picker reads `siblingValues.language` to filter the list by the BCP-47
 * code chosen in the Language picker on the same node. Optional;
 * undefined when the caller didn't bother building the index.
 */
export type SiblingValuesByTypeName = Record<string, string | null | undefined>;

interface Props {
  param: ModuleParameter;
  value: string;
  kind: ParamKind;
  applicationId: number;
  readOnly: boolean;
  onChange: (next: { value: string; kind: ParamKind }) => void;
  siblingValues?: SiblingValuesByTypeName;
}

export function ParamEditor({
  param,
  value,
  kind,
  applicationId,
  readOnly,
  onChange,
  siblingValues,
}: Props) {
  // A 'prompttext' kind can also be promoted by the caller for a plain
  // string parameter (voicebot prompts before migration 026); make sure the
  // switch then offers Text / Variable instead of String / Variable.
  const baseKinds = kindsForType(param.typeId, param.typeName);
  const kinds = kind === 'prompttext' && !baseKinds.some((k) => k.kind === 'prompttext')
    ? PROMPT_TEXT_KINDS
    : baseKinds;
  const showSwitch = kinds.length > 1 && !readOnly;

  // Local kind state so that when the user toggles to a different kind we
  // don't yet overwrite the value (legacy behaviour: switching kind clears
  // the displayed value to the corresponding kind's last input, which on a
  // fresh switch is empty).
  const [localKind, setLocalKind] = useState<ParamKind>(kind);
  useEffect(() => setLocalKind(kind), [kind]);

  const switchKind = (k: ParamKind) => {
    setLocalKind(k);
    onChange({ value: '', kind: storedKindFor(k) });
  };

  const editor = renderEditor(
    param,
    value,
    localKind,
    applicationId,
    readOnly,
    (v) => onChange({ value: v, kind: storedKindFor(localKind) }),
    siblingValues,
  );

  // The kind switch (Int / Variable, Voice / Variable, ...) sits inline to
  // the right of the editor instead of on its own row below it. That keeps
  // related controls visually grouped and saves a row of vertical space in
  // the properties panel. For editors that span multiple rows (KVP, multi-
  // line textareas) the switch still aligns to the first row of the input
  // because the wrapper uses `align-items: flex-start`.
  if (!showSwitch) {
    return <>{editor}</>;
  }
  return (
    <div className="fb-input-row">
      <div className="fb-input-row-main">{editor}</div>
      <div className="fb-kind-switch fb-kind-switch--inline">
        {kinds.map((k) => (
          <button
            key={k.kind}
            className={`fb-kind-btn${k.kind === localKind ? ' fb-kind-btn--active' : ''}`}
            onClick={() => switchKind(k.kind)}
            type="button"
            title={`Switch to ${k.label}`}
          >
            {k.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function renderEditor(
  param: ModuleParameter,
  value: string,
  kind: ParamKind,
  applicationId: number,
  readOnly: boolean,
  commit: (v: string) => void,
  siblingValues?: SiblingValuesByTypeName,
) {
  switch (kind) {
    case 'string':
      return <TextEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'prompttext':
      return (
        <PromptTextEditor
          value={value}
          readOnly={readOnly}
          commit={commit}
          applicationId={applicationId}
          placeholder={param.description || 'Text to speak, e.g. Dus je bent in ${City}.'}
        />
      );
    case 'int':
      return <NumberEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'destination':
      return (
        <TextEditor
          value={value}
          readOnly={readOnly}
          commit={commit}
          placeholder="e.g. 31630074770"
        />
      );
    case 'hidden':
      return <PasswordEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'bit':
      return <BoolEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'dtmf':
      return (
        <SelectEditor
          value={value}
          readOnly={readOnly}
          commit={commit}
          options={DTMF_OPTIONS}
        />
      );
    case 'date':
      return <DateEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'time':
      return <TimeEditor value={value} readOnly={readOnly} commit={commit} />;
    case 'divider':
      return (
        <div className="fb-divider-info">{param.description || 'Section divider'}</div>
      );
    case 'variable':
      return (
        <LookupEditor
          kind="variable"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Variable"
        />
      );
    case 'voicefile':
      return (
        <LookupEditor
          kind="audiofile"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Voice"
        />
      );
    case 'huntgroup':
      return (
        <LookupEditor
          kind="huntgroup"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Huntgroup"
        />
      );
    case 'agent':
      return (
        <LookupEditor
          kind="agent"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Agent"
        />
      );
    case 'weekplan':
      return (
        <LookupEditor
          kind="weekplan"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Weekplan"
        />
      );
    case 'calendar':
      return (
        <LookupEditor
          kind="calendar"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Calendar"
        />
      );
    case 'flow':
      return (
        <LookupEditor
          kind="flow"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Flow"
        />
      );
    case 'application':
      return (
        <LookupEditor
          kind="application"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Application"
        />
      );
    case 'kvp':
      return (
        <KvpEditor
          value={value}
          fallbackValue={param.defaultValue || ''}
          readOnly={readOnly}
          commit={commit}
        />
      );
    case 'simpledb':
      return (
        <DbLookupEditor
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
        />
      );
    case 'simpledbschedule':
      return (
        <DbLookupScheduleEditor
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
        />
      );
    case 'variablelist':
      return (
        <VariableListEditor
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
        />
      );
    case 'slotmapping':
      return (
        <SlotMappingEditor
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          recognizerKey={siblingValues?.recognizer ?? null}
          commit={commit}
        />
      );
    case 'recognizer':
      return (
        <LookupEditor
          kind="recognizer"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Recognizer"
        />
      );
    case 'language':
      return (
        <LanguageEditor
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
        />
      );
    case 'voicepreset':
      return (
        <LookupEditor
          kind="voicepreset"
          value={value}
          readOnly={readOnly}
          applicationId={applicationId}
          commit={commit}
          placeholder="Select Voice preset"
          // When a sibling Language parameter is set on the same node,
          // narrow the list to voices that advertise support for that
          // language. The picker still renders all voices when the
          // sibling is missing or empty.
          languageFilter={siblingValues?.language ?? null}
        />
      );
    // Final catch-all for parameter kinds we don't recognise yet — show a
    // raw JSON / text area so the value is at least visible & editable.
    default:
      return (
        <TextareaEditor
          value={value}
          readOnly={readOnly}
          commit={commit}
          placeholder="Unsupported parameter type — edit raw value"
        />
      );
  }
}

// --- primitive editors -------------------------------------------------

function TextEditor({
  value,
  readOnly,
  commit,
  placeholder,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      className="fb-input"
      value={local}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (!readOnly && local !== value) commit(local);
      }}
    />
  );
}

// KeyValuePair editor — mirrors the legacy KO ParameterEditorKeyValuePair.
// The stored value is a JSON document `{ type, placeholder?, value, options }`
// where `type` is "combo" or "radio" and `options` is a `{ key: label }` map.
// The user only changes `value`; type/placeholder/options remain as defined
// in the module's DefaultValue.
interface KvpDoc {
  type: 'combo' | 'radio' | string;
  placeholder?: string;
  value: string;
  options: Record<string, string>;
}

function parseKvp(raw: string): KvpDoc | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Partial<KvpDoc>;
    if (!obj || typeof obj !== 'object') return null;
    if (typeof obj.type !== 'string') return null;
    return {
      type: obj.type,
      placeholder: typeof obj.placeholder === 'string' ? obj.placeholder : undefined,
      value: obj.value == null ? '' : String(obj.value),
      options: (obj.options && typeof obj.options === 'object') ? (obj.options as Record<string, string>) : {},
    };
  } catch {
    return null;
  }
}

function KvpEditor({
  value,
  fallbackValue,
  readOnly,
  commit,
}: {
  value: string;
  fallbackValue: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const doc = useMemo<KvpDoc | null>(() => {
    return parseKvp(value) || parseKvp(fallbackValue);
  }, [value, fallbackValue]);

  if (!doc) {
    return (
      <div className="fb-error-inline">
        Could not parse Key-Value Pair definition.
      </div>
    );
  }

  const setValue = (next: string) => {
    if (readOnly) return;
    const out: KvpDoc = { ...doc, value: next };
    commit(JSON.stringify(out));
  };

  const entries = Object.entries(doc.options);

  if (doc.type === 'radio') {
    return (
      <div className="fb-kvp-radios">
        {entries.map(([key, label]) => (
          <label key={key} className="fb-kvp-radio">
            <input
              type="radio"
              name={`kvp_${key}_${entries.length}`}
              value={key}
              checked={doc.value === key}
              disabled={readOnly}
              onChange={() => setValue(key)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    );
  }

  // combo / fallback
  return (
    <select
      className="fb-input"
      value={doc.value}
      disabled={readOnly}
      onChange={(e) => setValue(e.target.value)}
    >
      {doc.placeholder && <option value="">{doc.placeholder}</option>}
      {entries.map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

function TextareaEditor({
  value,
  readOnly,
  commit,
  placeholder,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <textarea
      className="fb-textarea"
      rows={4}
      value={local}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (!readOnly && local !== value) commit(local);
      }}
    />
  );
}

function NumberEditor({
  value,
  readOnly,
  commit,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      type="number"
      className="fb-input"
      value={local}
      readOnly={readOnly}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (!readOnly && local !== value) commit(local);
      }}
    />
  );
}

function PasswordEditor({
  value,
  readOnly,
  commit,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  const [reveal, setReveal] = useState(false);
  useEffect(() => setLocal(value), [value]);
  return (
    <div className="fb-pw">
      <input
        type={reveal ? 'text' : 'password'}
        className="fb-input"
        value={local}
        readOnly={readOnly}
        autoComplete="new-password"
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (!readOnly && local !== value) commit(local);
        }}
      />
      <button
        type="button"
        className="fb-btn fb-btn--ghost"
        onClick={() => setReveal((r) => !r)}
        title={reveal ? 'Hide value' : 'Reveal value'}
      >
        {reveal ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function BoolEditor({
  value,
  readOnly,
  commit,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const checked = value === '1' || value === 'true';
  return (
    <label className="fb-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={readOnly}
        onChange={(e) => commit(e.target.checked ? '1' : '0')}
      />
      <span>Enabled</span>
    </label>
  );
}

function DateEditor({
  value,
  readOnly,
  commit,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      type="date"
      className="fb-input"
      value={local}
      readOnly={readOnly}
      onChange={(e) => {
        setLocal(e.target.value);
        if (!readOnly) commit(e.target.value);
      }}
    />
  );
}

/**
 * Time-of-day editor. The flow engine stores 'time' parameters as minutes
 * since 00:00 (e.g. 60 for 01:00, 90 for 01:30) so that downstream rules
 * can do plain integer comparisons. The editor itself uses a native time
 * input for the UX, so we translate at the boundary:
 *   - load:   minutes-string  →  "HH:MM"  (value displayed in the input)
 *   - commit: "HH:MM"          →  minutes-string (what we save back)
 *
 * For backwards-compatibility we also accept "HH:MM" coming in as the
 * stored value: any record that wasn't migrated yet keeps showing the
 * right time, and the next save normalises it to minutes.
 */
function TimeEditor({
  value,
  readOnly,
  commit,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
}) {
  const [local, setLocal] = useState(() => storedToHHMM(value));
  useEffect(() => setLocal(storedToHHMM(value)), [value]);
  return (
    <input
      type="time"
      className="fb-input"
      value={local}
      readOnly={readOnly}
      onChange={(e) => {
        const next = e.target.value;
        setLocal(next);
        if (!readOnly) commit(hhmmToStored(next));
      }}
    />
  );
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function clampInt(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.trunc(n)));
}

/**
 * Convert whatever is stored on disk into the "HH:MM" string the time
 * input expects. Accepts an integer minutes-since-midnight value (modern
 * format) or a legacy "HH:MM" string. Exported so the node summary can
 * reuse it without duplicating the conversion.
 */
export function storedToHHMM(raw: string): string {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (trimmed === "") return "";
  if (trimmed.includes(":")) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (!m) return "";
    return `${pad2(clampInt(parseInt(m[1], 10), 0, 23))}:${pad2(clampInt(parseInt(m[2], 10), 0, 59))}`;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return "";
  const total = clampInt(n, 0, 24 * 60 - 1);
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
}

/**
 * Convert the time input's "HH:MM" output into the integer minutes-string
 * we persist. Empty input clears the parameter (no implicit "0").
 */
function hhmmToStored(hhmm: string): string {
  if (!hhmm) return "";
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return "";
  const minutes = clampInt(parseInt(m[1], 10), 0, 23) * 60 + clampInt(parseInt(m[2], 10), 0, 59);
  return String(minutes);
}

function SelectEditor({
  value,
  readOnly,
  commit,
  options,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
  options: Array<{ id: string; name: string }>;
}) {
  return (
    <select
      className="fb-input"
      value={value || ''}
      disabled={readOnly}
      onChange={(e) => commit(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}

// --- lookup-driven editor ---------------------------------------------

function LookupEditor({
  kind,
  value,
  readOnly,
  applicationId,
  commit,
  placeholder,
  languageFilter,
}: {
  kind: LookupKind;
  value: string;
  readOnly: boolean;
  applicationId: number;
  commit: (v: string) => void;
  placeholder: string;
  /** When set + kind === 'voicepreset', narrows the voice list to the
   *  given BCP-47 language. Ignored for every other kind. */
  languageFilter?: string | null;
}) {
  const lang = kind === 'voicepreset' ? (languageFilter ?? null) : null;
  const { items, loading, error } = useLookup(kind, applicationId, { language: lang });
  const [createOpen, setCreateOpen] = useState(false);

  // Voice presets group by their primaryLanguage so the dropdown shows
  // a tidy <optgroup label="nl"> ... <optgroup label="en"> ... layout
  // when the list isn't already filtered. Variables already arrive with
  // their `category` set; for everything else this is a no-op.
  const grouped = useMemo(() => {
    if (kind === 'voicepreset') {
      const enriched = items.map((it) => ({
        ...it,
        category: it.category ?? it.primaryLanguage ?? null,
      }));
      return groupByCategory(enriched);
    }
    return groupByCategory(items);
  }, [items, kind]);

  if (error) {
    return <div className="fb-error-inline">Failed to load {kind}: {error}</div>;
  }

  // The "+ New variable…" sentinel is only offered on variable pickers
  // (and never when readOnly). Picking it opens the modal; on success
  // we commit the new id to the parent and the picker is invalidated.
  const showNewOption = kind === 'variable' && !readOnly;

  return (
    <>
      <select
        className="fb-input"
        value={value ?? ''}
        disabled={readOnly || loading}
        onChange={(e) => {
          const v = e.target.value;
          if (v === NEW_VARIABLE_OPTION) {
            setCreateOpen(true);
            return;
          }
          commit(v);
        }}
      >
        <option value="">{loading ? 'Loading…' : placeholder}</option>
        {grouped.map((g) =>
          g.category ? (
            <optgroup key={g.category} label={g.category}>
              {g.items.map((it) => (
                <option key={String(it.id)} value={String(it.id)}>
                  {it.name.trim()}
                </option>
              ))}
            </optgroup>
          ) : (
            g.items.map((it) => (
              <option key={String(it.id)} value={String(it.id)}>
                {it.name.trim()}
              </option>
            ))
          )
        )}
        {showNewOption && (
          <option value={NEW_VARIABLE_OPTION}>+ New variable…</option>
        )}
      </select>
      {showNewOption && (
        <VariableCreateModal
          open={createOpen}
          applicationId={applicationId}
          onCancel={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            commit(id);
          }}
        />
      )}
    </>
  );
}

interface Group {
  category: string | null;
  items: LookupItem[];
}

function groupByCategory(items: LookupItem[]): Group[] {
  const result: Group[] = [];
  let currentCat: string | null | undefined = undefined;
  let currentItems: LookupItem[] = [];
  for (const it of items) {
    const cat = (it.category ?? it.voiceCategory ?? null) as string | null;
    if (cat !== currentCat) {
      currentCat = cat;
      currentItems = [];
      result.push({ category: cat, items: currentItems });
    }
    currentItems.push(it);
  }
  return result;
}

// Human labels for BCP-47 codes. We keep the SPA-side mapping deliberately
// small — the runtime accepts any code, so anything outside this map is
// rendered as the raw code (still readable and unambiguous).
const LANG_LABELS: Record<string, string> = {
  nl: 'Nederlands',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  da: 'Dansk',
  sv: 'Svenska',
  no: 'Norsk',
  fi: 'Suomi',
  pl: 'Polski',
};

function languageLabel(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  // Match on the language sub-tag so "nl-BE" still resolves to "Nederlands".
  const base = lower.split(/[-_]/)[0];
  const label = LANG_LABELS[lower] ?? LANG_LABELS[base];
  return label ? `${label} (${trimmed})` : trimmed;
}

/**
 * BCP-47 language picker for Mod_VB_* nodes. Uses a `<datalist>` so the
 * user gets the known codes as suggestions but can still type any
 * custom code (the runtime accepts anything; the picker is a
 * convenience, not a constraint).
 */
function LanguageEditor({
  value,
  readOnly,
  applicationId,
  commit,
}: {
  value: string;
  readOnly: boolean;
  applicationId: number;
  commit: (v: string) => void;
}) {
  const { items, loading, error } = useLookup('language', applicationId);
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  // Build the suggestion list once: codes the API returned plus the
  // known LANG_LABELS keys (so "nl"/"en"/... show up even if the API
  // hasn't been touched yet).
  const suggestions = useMemo(() => {
    const set = new Set<string>(Object.keys(LANG_LABELS));
    for (const it of items) {
      const code = String(it.id).trim();
      if (code) set.add(code);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const datalistId = useMemo(
    () => `fb-lang-list-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  if (error) {
    return <div className="fb-error-inline">Failed to load languages: {error}</div>;
  }

  return (
    <>
      <input
        type="text"
        className="fb-input"
        list={datalistId}
        value={local}
        disabled={readOnly || loading}
        placeholder={loading ? 'Loading…' : 'BCP-47 code (e.g. nl, en, de-AT)'}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const next = local.trim();
          if (next !== value) commit(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const next = local.trim();
            if (next !== value) commit(next);
          }
        }}
        title={languageLabel(local) || 'BCP-47 language code'}
      />
      <datalist id={datalistId}>
        {suggestions.map((code) => (
          <option key={code} value={code}>
            {languageLabel(code)}
          </option>
        ))}
      </datalist>
    </>
  );
}
